// The listing popup's "Till jobbet" row (Restid till jobbet): the time from this home to the saved workplace,
// the chosen mode as a green pill and the others as plain text on the same line, arriving 08:30 on a weekday,
// with the trip a click away ("Visa resan"): drawn in the main photo's place on desktop and inside this card
// on phones (CommuteTrip.js, `trip` from the popup), or as a list of legs from a backend that sends no lines.
// Shows nothing until a workplace is set or while the routing engines don't answer.
import React, { useEffect, useRef, useState } from 'react';
import { track } from '../analytics/events.js';
import { commuteModeLabel, useCommuteRoute, useTravelAvailable } from './commute.js';
import { CommuteModeIcon } from './CommuteFilter.js';
import { TripPanel } from './CommuteTrip.js';
import './commute.css';

const LEG_STYLE = {
    WALK: { sv: 'Gå', en: 'Walk', color: 'var(--commute-walk)' },
    SUBWAY: { sv: 'T-bana', en: 'Metro', color: 'var(--commute-metro)' },
    METRO: { sv: 'T-bana', en: 'Metro', color: 'var(--commute-metro)' },
    BUS: { sv: 'Buss', en: 'Bus', color: 'var(--commute-bus)' },
    COACH: { sv: 'Buss', en: 'Coach', color: 'var(--commute-bus)' },
    TRAM: { sv: 'Spårvagn', en: 'Tram', color: 'var(--commute-tram)' },
    FERRY: { sv: 'Båt', en: 'Ferry', color: 'var(--commute-train)' },
};
const legStyle = (mode) => LEG_STYLE[mode] || { sv: 'Tåg', en: 'Train', color: 'var(--commute-train)' };

const minutesBucket = (minutes) => {
    if (minutes < 15) return '<15';
    if (minutes < 30) return '15-30';
    if (minutes < 45) return '30-45';
    if (minutes < 60) return '45-60';
    return '60+';
};

const placeName = (name, swedish, end) => {
    if (name === 'START') return swedish ? 'bostaden' : 'the home';
    if (name === 'END') return swedish ? 'jobbet' : 'work';
    return String(name || '').replace(/\s+T-bana$/, '') || (end ? (swedish ? 'jobbet' : 'work') : '');
};

const routeMinutes = (route) => ({
    transit: route?.transit?.minutes,
    bike: route?.bike?.minutes,
    car: route?.car?.minutes,
    walk: route?.walk?.minutes,
});


const ListingCommute = ({ listing, swedish, trip = null }) => {
    const available = useTravelAvailable();
    const { route, status, place, mode } = useCommuteRoute(listing);
    const [open, setOpen] = useState(false);
    const trackedRef = useRef('');

    const transit = route?.transit;
    const times = routeMinutes(route);
    const mainMinutes = times[mode];

    useEffect(() => {
        setOpen(false);
    }, [listing?.id]);
    useEffect(() => {
        if (status !== 'done' || !listing?.id || trackedRef.current === listing.id || mainMinutes == null) return;
        trackedRef.current = listing.id;
        track('commute.route', { mode, minutes: minutesBucket(mainMinutes) });
    }, [listing?.id, mainMinutes, mode, status]);

    if (available !== true || !place) return null;

    // Walking only when it's the chosen mode or under an hour.
    const order = [mode, ...['transit', 'bike', 'car', 'walk'].filter((key) => key !== mode && (key !== 'walk' || times.walk <= 60))];
    const shown = order.filter((key) => times[key] != null);
    const footnote = swedish
        ? `Till ${place.label}, fram 08:30 en vardag. Bil utan köer.`
        : `To ${place.label}, arriving 08:30 on a weekday. Car without traffic.`;
    return (
        <section className="lfv-commute" aria-live="polite" title={status === 'done' ? footnote : undefined}>
            <div className="lfv-commute-head">
                <span className="lfv-commute-title">{swedish ? 'Till jobbet' : 'To work'}</span>
                {status === 'loading' && <span className="lfv-commute-note">{swedish ? 'Räknar restid…' : 'Working out travel times…'}</span>}
                {status === 'error' && <span className="lfv-commute-note">{swedish ? 'Restiden gick inte att räkna ut just nu.' : 'Travel time is not available right now.'}</span>}
                {status === 'done' && shown.map((key, index) => (
                    <React.Fragment key={key}>
                        {index > 1 && <span className="lfv-commute-sep" aria-hidden="true">·</span>}
                        <span className={key === mode ? 'lfv-commute-mode is-main' : 'lfv-commute-alt'}>
                            {key === mode && <CommuteModeIcon mode={key} size={13} />}
                            {`${times[key]} min ${commuteModeLabel(key, swedish, true)}`}
                        </span>
                    </React.Fragment>
                ))}
                {status === 'done' && trip && !trip.phone && (
                    <button
                        type="button"
                        className={`lfv-commute-toggle${trip.shown ? ' is-on' : ''}`}
                        aria-pressed={trip.shown}
                        onClick={trip.shown ? trip.onHide : trip.onShow}
                    >
                        {trip.shown ? (swedish ? '✓ Resan visas' : '✓ Trip shown') : (swedish ? 'Visa resan ›' : 'Show trip ›')}
                    </button>
                )}
                {status === 'done' && (trip ? trip.phone : transit?.legs?.length > 0) && (
                    <button type="button" className="lfv-commute-toggle" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                        {open ? (swedish ? 'Dölj resan' : 'Hide trip') : (swedish ? 'Visa resan ›' : 'Show trip ›')}
                    </button>
                )}
            </div>
            {open && trip?.phone && (
                <TripPanel
                    route={route}
                    modes={trip.modes}
                    mode={trip.mode}
                    onModeChange={trip.onModeChange}
                    home={trip.home}
                    work={trip.work}
                    workLabel={place.label}
                    swedish={swedish}
                    dark={trip.dark}
                />
            )}
            {open && !trip && transit?.legs?.length > 0 && (
                <>
                    <div className="lfv-commute-legs" role="img" aria-label={transit.legs.map((leg) => `${legStyle(leg.mode)[swedish ? 'sv' : 'en']} ${leg.minutes} min`).join(', ')}>
                        {transit.legs.map((leg, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <span key={index} style={{ flex: Math.max(leg.minutes, 2), background: legStyle(leg.mode).color }}>
                                {leg.mode === 'WALK' ? `${leg.minutes}′` : `${leg.line || legStyle(leg.mode)[swedish ? 'sv' : 'en']} · ${leg.minutes}′`}
                            </span>
                        ))}
                    </div>
                    <ol className="lfv-commute-steps">
                        {transit.legs.map((leg, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={index}>
                                <span>
                                    {leg.mode === 'WALK'
                                        ? `${swedish ? 'Gå till' : 'Walk to'} ${placeName(leg.to, swedish, true)}`
                                        : `${legStyle(leg.mode)[swedish ? 'sv' : 'en']} ${leg.line}${leg.headsign ? ` ${swedish ? 'mot' : 'towards'} ${leg.headsign.replace(/\s+T-bana$/, '')}` : ''}, ${placeName(leg.from, swedish)} → ${placeName(leg.to, swedish, true)}`}
                                </span>
                                <span className="is-muted">{leg.minutes} min</span>
                            </li>
                        ))}
                    </ol>
                </>
            )}
            {status === 'done' && !(open && trip?.phone) && <span className="lfv-commute-note lfv-commute-foot">{footnote}</span>}
        </section>
    );
};

export default ListingCommute;
